/**
 * NotificationBell — sidebar footer bildirim tetikleyicisi (N2-02)
 *
 * SidebarThemeToggle desenini izler: expanded'da etiketli satır, collapsed'da
 * kare ikon buton; badge her iki durumda görünür (D-01). Açılış responsive
 * (D-02): masaüstü Radix Popover (footer'dan yukarı, side=top align=end),
 * mobil (useIsMobile 768) sağdan kayan Sheet.
 *
 * Badge (D-03): sayılı + marka rengi (brand-chartreuse amber, KIRMIZI DEĞİL).
 * Renk kararı D-03b/ui-ux-pro-max: amber zemin + koyu-teal metin kontrastı
 * ~8.5:1 (WCAG AAA); purple alternatif ~2.8:1 (AA'yı geçemez) → chartreuse.
 * Yeni bildirimde TEK-SEFER pulse (framer-motion, prefers-reduced-motion'a
 * saygılı; sonsuz animate-pulse KULLANILMAZ).
 */
import { useEffect, useState } from 'react'
import { Bell } from 'lucide-react'
import { motion, useReducedMotion } from 'framer-motion'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSidebar } from '@/components/ui/sidebar-context'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  useMarkNotificationRead,
  useNotificationsList,
  useUnreadCount,
} from '@/hooks/use-notifications'
import { cn } from '@/lib/utils'

import { NotificationList } from './notification-list'

/** Sayılı marka-renkli rozet; count>9 → "9+". Tek-sefer scale pulse. */
function UnreadBadge({ count, className }: { count: number; className?: string }) {
  const prefersReducedMotion = useReducedMotion()
  const label = count > 9 ? '9+' : String(count)

  return (
    <motion.span
      key={count}
      initial={prefersReducedMotion ? false : { scale: 0.6 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 500, damping: 22 }}
      className={cn(
        'inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1',
        'text-[10px] font-bold leading-none tabular-nums',
        'bg-brand-chartreuse text-brand-chartreuse-foreground',
        className
      )}
    >
      {label}
    </motion.span>
  )
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'unread' | 'all'>('unread')
  // Panel acikken tiklanip okundu sayilan bildirimler (#767): Okunmamis
  // sekmesinden aninda dusmesin, kullanici tam metni okuyabilsin. Panel
  // kapaninca sifirlanir; bir sonraki acilista yalniz gercekten okunmamislar kalir.
  const [readWhileOpen, setReadWhileOpen] = useState<Set<string>>(() => new Set())
  const isMobile = useIsMobile()
  const { visualState } = useSidebar()
  const isCollapsed = visualState === 'collapsed'

  const { data: unreadCount = 0 } = useUnreadCount()
  const list = useNotificationsList({ enabled: open })
  const { mutate: markRead } = useMarkNotificationRead()

  // Toast'a tıklayınca kutuyu aç (N2-03 köprüsü — hook 'sensei:notification:open' yayar)
  useEffect(() => {
    const h = () => setOpen(true)
    window.addEventListener('sensei:notification:open', h)
    return () => window.removeEventListener('sensei:notification:open', h)
  }, [])

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setReadWhileOpen(new Set())
  }

  const handleRead = (id: string) => {
    setReadWhileOpen((prev) => new Set(prev).add(id))
    markRead(id)
  }

  const hasUnread = unreadCount > 0
  const ariaLabel = `Bildirimler${hasUnread ? `, ${unreadCount} okunmamış` : ''}`

  // Sekme filtresi (client-side, D-T3): Okunmamış = is_read=false, Tümü = hepsi
  const allItems = list.data ?? []
  const unreadItems = allItems.filter((i) => !i.is_read)
  const unreadTabItems = allItems.filter((i) => !i.is_read || readWhileOpen.has(i.id))
  const shown = tab === 'unread' ? unreadTabItems : allItems

  // Trigger butonu — expanded vs collapsed iki layout (SidebarThemeToggle deseni).
  const triggerButton = isCollapsed ? (
    <button
      type="button"
      aria-label={ariaLabel}
      className="relative grid size-9 place-items-center rounded-lg border border-sidebar-border bg-sidebar-accent text-sidebar-foreground/70 transition-colors hover:text-sidebar-accent-foreground"
    >
      <Bell className="size-4" />
      {hasUnread && (
        <UnreadBadge count={unreadCount} className="absolute -right-1 -top-1" />
      )}
    </button>
  ) : (
    <button
      type="button"
      aria-label={ariaLabel}
      className="flex w-full items-center gap-2.5 rounded-lg border border-sidebar-border bg-sidebar-accent px-2.5 py-2 transition-opacity hover:opacity-85"
    >
      <Bell className="size-4 text-sidebar-foreground/70" />
      <span className="text-xs font-medium text-sidebar-foreground">Bildirimler</span>
      {hasUnread && <UnreadBadge count={unreadCount} className="ml-auto" />}
    </button>
  )

  // Okunmamış/Tümü sekmeleri — client-side filtre, varsayılan Okunmamış (D-T3).
  const tabsElement = (
    <Tabs value={tab} onValueChange={(v) => setTab(v as 'unread' | 'all')}>
      <TabsList className="gap-1">
        <TabsTrigger
          value="unread"
          className="data-[state=active]:bg-muted data-[state=active]:text-foreground"
        >
          Okunmamış{unreadItems.length ? ` (${unreadItems.length})` : ''}
        </TabsTrigger>
        <TabsTrigger
          value="all"
          className="data-[state=active]:bg-muted data-[state=active]:text-foreground"
        >
          Tümü
        </TabsTrigger>
      </TabsList>
    </Tabs>
  )

  const listElement = (
    <NotificationList
      items={shown}
      isLoading={list.isLoading}
      isError={list.isError}
      onRetry={() => list.refetch()}
      onRead={handleRead}
      emptyLabel={tab === 'unread' ? 'Okunmamış bildirimin yok' : undefined}
    />
  )

  const wrapperClass = isCollapsed ? 'flex justify-center px-1 py-1' : 'px-2 py-1'

  // Mobil: sağdan kayan tam-yükseklik Sheet (D-02).
  if (isMobile) {
    return (
      <div className={wrapperClass}>
        <Sheet open={open} onOpenChange={handleOpenChange}>
          <SheetTrigger asChild>{triggerButton}</SheetTrigger>
          <SheetContent side="right" className="flex w-full max-w-sm flex-col p-0">
            <SheetHeader className="border-b px-4 py-3 text-left">
              <SheetTitle className="text-sm font-semibold">Bildirimler</SheetTitle>
              <SheetDescription className="sr-only">
                Sana gelen bildirimlerin listesi
              </SheetDescription>
              <div className="pt-1">{tabsElement}</div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto">{listElement}</div>
          </SheetContent>
        </Sheet>
      </div>
    )
  }

  // Masaüstü: footer'dan yukarı açılan Popover (D-02).
  return (
    <div className={wrapperClass}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent side="top" align="end" sideOffset={8} className="w-80 p-0">
          <div className="flex flex-col gap-2 border-b px-4 py-3">
            <span className="text-sm font-semibold">Bildirimler</span>
            {tabsElement}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">{listElement}</div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
